import { useEffect, useState, useRef } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { lemmaPrefix } from '../../../lib/lemmaPrefix';

/**
 * Collapsible "show another example" hint for context-recognition style
 * questions (§3.24). Lives in shared/ because it's tied to the
 * ContextRecognition question type — onboarding Stage 1 today, and any
 * future review-side context_recognition variant will reuse it without
 * a copy. See §3.23 Question-Type Contract.
 *
 * Behavior:
 *  - Default collapsed. Toggling open marks `hintUsedRef` true; the
 *    parent reads it at submit time and ships the flag along with the
 *    answer so the back-end can log analytics (no FSRS penalty).
 *  - On first open: render any `initialExamples` straight away. If
 *    none are available, call `onRequestMore` (async) to lazy-generate
 *    one from the back-end; cache the result in component state so
 *    follow-up opens don't re-fetch.
 *
 * Props:
 *   lemma             — the target word; bolded in any rendered example.
 *   initialExamples   — array of cached sentence strings, or empty/null.
 *   onRequestMore     — async () => string | null. Called on first open
 *                       when initialExamples is empty.
 *   onHintUsed        — () => void. Called the first time the panel
 *                       opens, so the parent can flag the submission.
 *   open / onOpenChange         — controlled open state. Optional; when
 *                       provided, the parent owns the toggle (used by
 *                       OnboardingActivity for live-session sync).
 *   fetched / onFetchedChange   — controlled fetched-example state.
 *                       Same role: lifts the lazily-generated sentence
 *                       up so it can ride on progress_state to the peer.
 */
export default function ExampleSentenceHint({
  lemma,
  initialExamples,
  onRequestMore,
  onHintUsed,
  open: controlledOpen,
  fetched: controlledFetched,
  onOpenChange,
  onFetchedChange,
}) {
  const isOpenControlled = controlledOpen !== undefined;
  const isFetchedControlled = controlledFetched !== undefined;
  const [localOpen, setLocalOpen] = useState(false);
  const [localFetched, setLocalFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasFiredHintUsedRef = useRef(false);

  const open = isOpenControlled ? !!controlledOpen : localOpen;
  const fetched = isFetchedControlled ? controlledFetched : localFetched;
  const setOpen = (v) => {
    if (typeof onOpenChange === 'function') onOpenChange(v);
    if (!isOpenControlled) setLocalOpen(v);
  };
  const setFetched = (v) => {
    if (typeof onFetchedChange === 'function') onFetchedChange(v);
    if (!isFetchedControlled) setLocalFetched(v);
  };

  const cachedExample = initialExamples?.[0] || null;
  const displayed = cachedExample || fetched;

  // Auto-fetch when `open` flips to true from an external source
  // (controlled prop driven by a peer's peer_intent). Without this
  // effect, only the local toggle button triggers the fetcher; a
  // teacher-driven open would leave the student stuck on the loading
  // spinner forever. Gated on having no cached and no fetched yet.
  useEffect(() => {
    if (!open) return;
    if (cachedExample || fetched != null) return;
    if (typeof onRequestMore !== 'function') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const text = await onRequestMore();
        if (!cancelled) setFetched(text || '');
      } catch {
        if (!cancelled) setFetched('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = async () => {
    const opening = !open;
    setOpen(opening);
    if (!opening) return;

    if (!hasFiredHintUsedRef.current) {
      hasFiredHintUsedRef.current = true;
      if (typeof onHintUsed === 'function') onHintUsed();
    }

    // Lazy fetch only when the cache is empty AND we haven't fetched yet.
    if (!cachedExample && fetched == null && typeof onRequestMore === 'function') {
      setLoading(true);
      try {
        const text = await onRequestMore();
        setFetched(text || '');
      } catch {
        setFetched('');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="mt-3 text-sm">
      <button
        type="button"
        onClick={toggle}
        // pointer-events-auto: re-enable this toggle when the activity is
        // observer-locked (pointer-events-none wrapper) so the teacher can
        // open/close the example too — it syncs via peer_intent.
        className="pointer-events-auto inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
        <span className="underline underline-offset-2">
          {open ? 'Hide example' : 'Show another example'}
        </span>
      </button>

      {open && (
        <div className="mt-2 pl-5 pr-2 py-2 rounded-md bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 italic">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Coming up with an example…</span>
            </div>
          ) : displayed ? (
            <p className="font-serif text-gray-700 dark:text-gray-300 leading-relaxed">
              {highlight(displayed, lemma)}
            </p>
          ) : (
            <p className="text-gray-400 dark:text-gray-500 italic">
              Couldn't find another example for this word.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function highlight(text, lemma) {
  if (!lemma) return text;
  const prefix = lemmaPrefix(lemma);
  if (!prefix) return text;
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Capture group so split keeps the matched tokens in the result.
  const splitter = new RegExp(`(\\b${escaped}\\w*\\b)`, 'gi');
  const matcher = new RegExp(`^\\b${escaped}\\w*\\b$`, 'i');
  return text.split(splitter).map((part, i) =>
    matcher.test(part) ? (
      <strong key={i} className="font-semibold text-gray-900 dark:text-white">
        {part}
      </strong>
    ) : (
      part
    )
  );
}
